import ResponsiveImage from "@/components/ResponsiveImage";
import { COMPANY_REVIEWS } from "@shared/companyFacts";
import { packageLandingData } from "@/lib/packageLandingData";
import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "wouter";
import {
  Menu,
  X,
  Phone,
  ChevronDown,
  Sun,
  Snowflake,
  Home,
  Layers,
  Ruler,
  ThermometerSun,
  Droplets,
  Wind,
  FileText,
  ArrowRight,
  Maximize2,
  BookOpen,
  MapPin,
  CheckSquare,
  Sparkles,
  Factory,
  Search,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { IMAGES } from "@/lib/images";
import { useOffert } from "@/components/OffertModal";
import { useSearch } from "@/components/Layout";

/* ─── Mega Menu Data Structure ─── */

interface SubMenuItem {
  label: string;
  href: string;
  desc: string;
  icon: React.ElementType;
}

interface MenuCategory {
  heading: string;
  items: SubMenuItem[];
}

interface FeaturedItem {
  title: string;
  desc: string;
  href: string;
  image: string;
}

const megaMenuCategories: MenuCategory[] = [
  {
    heading: "Paket",
    items: [
      {
        label: "Våra paket",
        href: "/uterum/vara-paket/",
        desc: `Jämför alla ${Object.keys(packageLandingData).length} paket`,
        icon: Layers,
      },
      ...Object.values(packageLandingData).map(data => ({
        label: data.name,
        href: `/uterum/${data.slug}/`,
        desc: data.season,
        icon: data.icon,
      })),
    ],
  },
  {
    heading: "Guider",
    items: [
      {
        label: "Allt om uterum",
        href: "/uterum/",
        desc: "Komplett guide från A till Ö",
        icon: BookOpen,
      },
      {
        label: "Bygglov",
        href: "/uterum/bygglov/",
        desc: "Regler och ansökan 2026",
        icon: FileText,
      },
      {
        label: "Storlek",
        href: "/uterum/storlek/",
        desc: "Välj rätt storlek för ditt hus",
        icon: Maximize2,
      },
      {
        label: "Uterum vs Orangeri",
        href: "/uterum/uterum-vs-orangeri/",
        desc: "Skillnader och likheter",
        icon: Layers,
      },
      {
        label: "Checklista",
        href: "/uterum/checklista/",
        desc: "Allt du behöver tänka på",
        icon: CheckSquare,
      },
    ],
  },
  {
    heading: "Kunskap",
    items: [
      {
        label: "Vanliga frågor",
        href: "/uterum/fragor/",
        desc: "Svar och guider om uterum",
        icon: BookOpen,
      },
      {
        label: "Vår fabrik",
        href: "/uterum/var-fabrik/",
        desc: "Svensk tillverkning i Eskilstuna",
        icon: Factory,
      },
      {
        label: "Våra paket",
        href: "/uterum/vara-paket/",
        desc: "Jämför 6 paket sida vid sida",
        icon: Layers,
      },
      {
        label: "AI-visualisering",
        href: "/uterum/visualisera/",
        desc: "Se uterum på ditt hus med AI",
        icon: Sparkles,
      },
    ],
  },
  {
    heading: "Konstruktion",
    items: [
      {
        label: "Glaspartier",
        href: "/uterum/glaspartier/",
        desc: "Skjut-, vik- och lyftpartier",
        icon: Ruler,
      },
      {
        label: "Grund",
        href: "/uterum/grund/",
        desc: "Platta, plintar och bjälklag",
        icon: Layers,
      },
      {
        label: "Tak",
        href: "/uterum/tak/",
        desc: "Glas, kanalplast och isolerat",
        icon: Home,
      },
      {
        label: "Uppvärmning",
        href: "/uterum/uppvarmning/",
        desc: "Golvvärme och värmekällor",
        icon: ThermometerSun,
      },
      {
        label: "Kondens",
        href: "/uterum/kondens/",
        desc: "Orsaker och lösningar",
        icon: Droplets,
      },
      {
        label: "Ventilation",
        href: "/uterum/oisolerat/",
        desc: "Luftflöde och klimatkontroll",
        icon: Wind,
      },
    ],
  },
];

const megaMenuFeatured: FeaturedItem[] = [
  {
    title: "Inspiration",
    desc: "Se våra senaste projekt",
    href: "/uterum/inspiration/",
    image: IMAGES.projekt33,
  },
  {
    title: "Referenser",
    desc: "Kundcase och omdömen",
    href: "/referenser/uterum/",
    image: IMAGES.projekt17,
  },
];

/* Top-level nav items */
const topNavItems = [
  { label: "Uterum", href: "/uterum/", hasMegaMenu: true },
  { label: "Bygglov", href: "/uterum/bygglov/", hasMegaMenu: false },
  { label: "Inspiration", href: "/uterum/inspiration/", hasMegaMenu: false },
  { label: "Referenser", href: "/referenser/uterum/", hasMegaMenu: false },
  { label: "Frågor", href: "/uterum/fragor/", hasMegaMenu: false },
  { label: "Om oss", href: "/om-oss/", hasMegaMenu: false },
];

/* Cities — same list in the desktop mega menu and the mobile Uterum accordion */
const cityItems = [
  { label: "Mälardalen", href: "/uterum/malardalen/" },
  { label: "Stockholm", href: "/uterum/stockholm/" },
  { label: "Göteborg", href: "/uterum/goteborg/" },
  { label: "Malmö", href: "/uterum/malmo/" },
  { label: "Uppsala", href: "/uterum/uppsala/" },
  { label: "Helsingborg", href: "/uterum/helsingborg/" },
  { label: "Västerås", href: "/uterum/vasteras/" },
  { label: "Örebro", href: "/uterum/orebro/" },
  { label: "Linköping", href: "/uterum/linkoping/" },
  { label: "Eskilstuna", href: "/uterum/eskilstuna/" },
  { label: "Södertälje", href: "/uterum/sodertalje/" },
  { label: "Nyköping", href: "/uterum/nykoping/" },
  { label: "Strängnäs", href: "/uterum/strangnas/" },
  { label: "Enköping", href: "/uterum/enkoping/" },
  { label: "Katrineholm", href: "/uterum/katrineholm/" },
  { label: "Sala", href: "/uterum/sala/" },
  { label: "Köping", href: "/uterum/koping/" },
  { label: "Arboga", href: "/uterum/arboga/" },
  { label: "Flen", href: "/uterum/flen/" },
  { label: "Gnesta", href: "/uterum/gnesta/" },
  { label: "Trosa", href: "/uterum/trosa/" },
];

export default function Header() {
  const { openOffert } = useOffert();
  const { openSearch } = useSearch();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileAccordion, setMobileAccordion] = useState<string | null>(null);
  const [mobileSubAccordion, setMobileSubAccordion] = useState<string | null>(
    null
  );
  const [location] = useLocation();
  const dropdownTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [megaMenuOpen, setMegaMenuOpen] = useState(false);

  useEffect(() => {
    setMobileOpen(false);
    setMobileAccordion(null);
    setMobileSubAccordion(null);
    setMegaMenuOpen(false);
  }, [location]);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  const handleMegaEnter = () => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
      dropdownTimeoutRef.current = null;
    }
    setMegaMenuOpen(true);
  };

  const handleMegaLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setMegaMenuOpen(false);
    }, 200);
  };

  const toggleMobileAccordion = (label: string) => {
    setMobileAccordion(current => {
      const next = current === label ? null : label;
      if (next !== current) setMobileSubAccordion(null);
      return next;
    });
  };

  const toggleMobileSubAccordion = (label: string) => {
    setMobileSubAccordion(current => (current === label ? null : label));
  };

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-border/50">
      {/* Top bar */}
      <div className="bg-navy text-white text-xs sm:text-sm">
        <div className="container flex items-center justify-between py-1.5">
          <div className="hidden lg:flex items-center gap-6">
            <span>erfarenhet sedan 2012</span>
            <span className="w-px h-3.5 bg-white/30" />
            <span>cirka 18 000 inglasade balkonger</span>
            <span className="w-px h-3.5 bg-white/30" />
            <span>{COMPANY_REVIEWS.ratingLabel}</span>
          </div>
          <div className="flex lg:hidden items-center gap-3 text-white/80">
            <span>erfarenhet sedan 2012</span>
            <span className="w-px h-3 bg-white/30" />
            <span>cirka 18 000 inglasade balkonger</span>
            <span className="w-px h-3 bg-white/30" />
            <span>{COMPANY_REVIEWS.ratingLabel}</span>
          </div>
          <a
            href="tel:+46841026111"
            className="flex items-center gap-1.5 hover:underline shrink-0"
            aria-label="Ring oss: 08-410 26 111"
          >
            <Phone className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">08-410 26 111</span>
          </a>
        </div>
      </div>

      {/* Main nav */}
      <div className="container flex items-center justify-between py-2.5 lg:py-3">
        <Link href="/" className="shrink-0 group">
          <ResponsiveImage sizes="(min-width: 1024px) 176px, 151px" loading="eager"
              src="/manus-storage/logo-header_6798f633.webp"
              alt="Nordiska Uterum logotyp"
              className="h-10 lg:h-12 w-auto group-hover:opacity-80 transition-opacity duration-200"
              width={280}
              height={89}
              decoding="async"
            />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-0.5">
          {topNavItems.map(item =>
            item.hasMegaMenu ? (
              <div
                key={item.label}
                className="relative"
                onMouseEnter={handleMegaEnter}
                onMouseLeave={handleMegaLeave}
              >
                <Link
                  href={item.href}
                  className={cn(
                    "px-3 py-2 text-sm font-medium rounded-md transition-all duration-200 inline-flex items-center gap-1",
                    location === item.href ||
                      (item.href !== "/" && location.startsWith("/uterum"))
                      ? "text-navy bg-sand"
                      : "text-muted-foreground hover:text-navy hover:bg-sand/50 hover:shadow-sm"
                  )}
                >
                  {item.label}
                  <ChevronDown
                    className={cn(
                      "w-3.5 h-3.5 transition-transform duration-200",
                      megaMenuOpen ? "rotate-180" : ""
                    )}
                  />
                </Link>

                {/* ═══ MEGA MENU ═══ */}
                <div
                  className={cn(
                    "fixed left-0 right-0 top-[calc(100%)] pt-0 transition-all duration-250 ease-out",
                    megaMenuOpen
                      ? "opacity-100 visible translate-y-0"
                      : "opacity-0 invisible -translate-y-2 pointer-events-none"
                  )}
                  style={{ top: "auto" }}
                >
                  {/* Invisible bridge to prevent gap between nav item and menu */}
                  <div className="h-2" />
                  <div className="bg-white border-t border-border/30 shadow-xl">
                    <div className="container py-8">
                      <div className="grid grid-cols-12 gap-6">
                        {/* Categories */}
                        {megaMenuCategories.map(category => (
                          <div key={category.heading} className="col-span-2">
                            <span
                              className="text-xs font-bold text-navy/40 uppercase tracking-widest mb-4 block"
                              role="heading"
                              aria-level={3}
                            >
                              {category.heading}
                            </span>
                            <div className="flex flex-col gap-1">
                              {category.items.map(subItem => (
                                <Link
                                  key={subItem.href}
                                  href={subItem.href}
                                  className={cn(
                                    "group/item flex items-start gap-3 px-3 py-2.5 rounded-lg transition-all duration-200",
                                    location === subItem.href
                                      ? "bg-sand text-navy"
                                      : "hover:bg-sand/60 text-foreground/80 hover:text-navy hover:translate-x-0.5"
                                  )}
                                >
                                  <subItem.icon className="w-5 h-5 mt-0.5 shrink-0 text-navy/50 group-hover/item:text-rose transition-colors" />
                                  <div>
                                    <span className="text-sm font-semibold block leading-tight">
                                      {subItem.label}
                                    </span>
                                    <span className="text-xs text-muted-foreground leading-snug mt-0.5 block">
                                      {subItem.desc}
                                    </span>
                                  </div>
                                </Link>
                              ))}
                            </div>
                          </div>
                        ))}

                        {/* Featured column: images + CTA */}
                        <div className="col-span-2">
                          <span
                            className="text-xs font-bold text-navy/40 uppercase tracking-widest mb-4 block"
                            role="heading"
                            aria-level={3}
                          >
                            Utforska
                          </span>
                          <div className="flex flex-col gap-3">
                            {megaMenuFeatured.map(featured => (
                              <Link
                                key={featured.href}
                                href={featured.href}
                                className="group/feat relative overflow-hidden rounded-lg block"
                              >
                                <ResponsiveImage sizes="320px"
                                  src={featured.image}
                                  alt={featured.title}
                                  className="w-full h-28 object-cover transition-transform duration-300 group-hover/feat:scale-105"
                                  loading="lazy"
                                  width={1920}
                                  height={1080}
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-navy/80 via-navy/30 to-transparent" />
                                <div className="absolute bottom-0 left-0 right-0 p-3">
                                  <span className="text-white font-semibold text-sm block">
                                    {featured.title}
                                  </span>
                                  <span className="text-white/70 text-xs">
                                    {featured.desc}
                                  </span>
                                </div>
                              </Link>
                            ))}
                          </div>

                          {/* Städer */}
                          <div className="mt-4 pt-3 border-t border-border/30">
                            <div className="flex items-center gap-1.5 mb-2">
                              <MapPin className="w-3.5 h-3.5 text-rose" />
                              <span className="text-xs font-bold text-navy/40 uppercase tracking-widest">
                                Städer
                              </span>
                            </div>
                            <div className="flex flex-wrap gap-x-3 gap-y-1">
                              {cityItems.map(city => (
                                <Link
                                  key={city.href}
                                  href={city.href}
                                  className="text-xs text-muted-foreground hover:text-navy transition-colors"
                                >
                                  {city.label}
                                </Link>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Bottom CTA bar */}
                      <div className="mt-6 pt-5 border-t border-border/30 flex items-center justify-between">
                        <Link
                          href="/uterum/checklista/"
                          className="text-sm text-rose font-semibold hover:text-rose-dark transition-colors flex items-center gap-1.5 group/cta"
                        >
                          Ladda ner vår checklista innan du köper uterum
                          <ArrowRight className="w-4 h-4 group-hover/cta:translate-x-0.5 transition-transform" />
                        </Link>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <a
                            href="tel:+46841026111"
                            className="flex items-center gap-1.5 hover:text-navy transition-colors"
                          >
                            <Phone className="w-4 h-4" />
                            08-410 26 111
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <Link
                key={item.label}
                href={item.href}
                className={cn(
                  "px-3 py-2 text-sm font-medium rounded-md transition-all duration-200",
                  location === item.href ||
                    (item.href !== "/" && location.startsWith(item.href))
                    ? "text-navy bg-sand"
                    : "text-muted-foreground hover:text-navy hover:bg-sand/50 hover:shadow-sm"
                )}
              >
                {item.label}
              </Link>
            )
          )}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <button
            onClick={() => openSearch()}
            className="p-2 text-muted-foreground hover:text-navy transition-colors rounded-md hover:bg-sand/50"
            aria-label="Sök (Ctrl+K)"
            title="Sök (Ctrl+K)"
          >
            <Search className="w-4.5 h-4.5" />
          </button>
          <a
            href="tel:+46841026111"
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-navy transition-colors"
          >
            <Phone className="w-4 h-4" />
            <span>08-410 26 111</span>
          </a>
          <Button
            onClick={() => openOffert("header-desktop")}
            className="bg-rose hover:bg-rose-dark text-white font-medium px-5"
          >
            Ta kontakt
          </Button>
        </div>

        {/* Mobile actions */}
        <div className="flex lg:hidden items-center gap-2">
          <button
            onClick={() => openSearch()}
            className="p-2.5 text-navy min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label="Sök"
          >
            <Search className="w-5 h-5" />
          </button>
          <a
            href="tel:+46841026111"
            className="p-2.5 text-navy min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label="Ring oss"
          >
            <Phone className="w-5 h-5" />
          </a>
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="p-2 text-navy -mr-2"
            aria-label={mobileOpen ? "Stäng meny" : "Öppna meny"}
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>
      </div>

      {/* ═══ MOBILE NAV ═══ */}
      <div
        inert={!mobileOpen}
        aria-hidden={!mobileOpen}
        className={cn(
          "lg:hidden overflow-hidden transition-all duration-300 ease-in-out",
          mobileOpen
            ? "max-h-[calc(100vh-120px)] opacity-100"
            : "max-h-0 opacity-0"
        )}
      >
        <div className="border-t border-border bg-white overflow-y-auto max-h-[calc(100vh-120px)]">
          <nav className="container py-3 flex flex-col gap-1">
            {topNavItems.map(item =>
              item.hasMegaMenu ? (
                <div key={item.label}>
                  <button
                    onClick={() => toggleMobileAccordion(item.label)}
                    className={cn(
                      "w-full flex items-center justify-between px-4 py-3.5 text-base font-semibold rounded-lg transition-colors",
                      mobileAccordion === item.label
                        ? "text-navy bg-sand"
                        : "text-foreground hover:text-navy hover:bg-sand/50"
                    )}
                  >
                    <span>{item.label}</span>
                    <ChevronDown
                      className={cn(
                        "w-4 h-4 transition-transform duration-200",
                        mobileAccordion === item.label ? "rotate-180" : ""
                      )}
                    />
                  </button>
                  <div
                    className={cn(
                      "overflow-hidden transition-all duration-300",
                      mobileAccordion === item.label
                        ? "max-h-[1200px] opacity-100"
                        : "max-h-0 opacity-0"
                    )}
                  >
                    <div className="pl-2 pb-2 pt-1">
                      <Link
                        href={item.href}
                        onClick={() => setMobileOpen(false)}
                        className={cn(
                          "flex items-center px-4 py-3 rounded-lg text-sm font-medium transition-colors",
                          location === item.href
                            ? "text-navy bg-sand"
                            : "text-foreground/80 hover:text-navy hover:bg-sand/50"
                        )}
                      >
                        Allt om uterum
                      </Link>
                      {megaMenuCategories.map(category => (
                        <div key={category.heading}>
                          <button
                            onClick={() =>
                              toggleMobileSubAccordion(category.heading)
                            }
                            className={cn(
                              "w-full flex items-center justify-between px-4 py-3 text-sm font-bold uppercase tracking-widest rounded-lg transition-colors",
                              mobileSubAccordion === category.heading
                                ? "text-navy bg-sand/70"
                                : "text-navy/50 hover:text-navy hover:bg-sand/40"
                            )}
                          >
                            <span>{category.heading}</span>
                            <ChevronDown
                              className={cn(
                                "w-3.5 h-3.5 transition-transform duration-200",
                                mobileSubAccordion === category.heading
                                  ? "rotate-180"
                                  : ""
                              )}
                            />
                          </button>
                          <div
                            className={cn(
                              "overflow-hidden transition-all duration-300",
                              mobileSubAccordion === category.heading
                                ? "max-h-[800px] opacity-100"
                                : "max-h-0 opacity-0"
                            )}
                          >
                            <div className="pl-2 pb-1">
                              {category.items.map(subItem => (
                                <Link
                                  key={subItem.href}
                                  href={subItem.href}
                                  onClick={() => setMobileOpen(false)}
                                  className={cn(
                                    "flex items-start gap-3 px-4 py-3 rounded-lg transition-colors",
                                    location === subItem.href
                                      ? "text-navy bg-sand font-medium"
                                      : "text-foreground/80 hover:text-navy hover:bg-sand/50"
                                  )}
                                >
                                  <subItem.icon className="w-4.5 h-4.5 mt-0.5 shrink-0 text-navy/40" />
                                  <div>
                                    <span className="text-sm font-medium block leading-tight">
                                      {subItem.label}
                                    </span>
                                    <span className="text-xs text-muted-foreground leading-snug mt-0.5 block">
                                      {subItem.desc}
                                    </span>
                                  </div>
                                </Link>
                              ))}
                            </div>
                          </div>
                        </div>
                      ))}
                      <button
                        onClick={() => toggleMobileSubAccordion("Städer")}
                        className={cn(
                          "w-full flex items-center justify-between px-4 py-3 text-sm font-bold uppercase tracking-widest rounded-lg transition-colors",
                          mobileSubAccordion === "Städer"
                            ? "text-navy bg-sand/70"
                            : "text-navy/50 hover:text-navy hover:bg-sand/40"
                        )}
                      >
                        <span>Städer</span>
                        <ChevronDown
                          className={cn(
                            "w-3.5 h-3.5 transition-transform duration-200",
                            mobileSubAccordion === "Städer" ? "rotate-180" : ""
                          )}
                        />
                      </button>
                      <div
                        className={cn(
                          "overflow-hidden transition-all duration-300",
                          mobileSubAccordion === "Städer"
                            ? "max-h-[600px] opacity-100"
                            : "max-h-0 opacity-0"
                        )}
                      >
                        <div className="pl-2 pb-1 grid grid-cols-2 gap-x-2">
                          {cityItems.map(city => (
                            <Link
                              key={city.href}
                              href={city.href}
                              onClick={() => setMobileOpen(false)}
                              className={cn(
                                "flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm transition-colors",
                                location === city.href
                                  ? "text-navy bg-sand font-medium"
                                  : "text-foreground/80 hover:text-navy hover:bg-sand/50"
                              )}
                            >
                              <MapPin className="w-3.5 h-3.5 text-rose/60" />
                              {city.label}
                            </Link>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <Link
                  key={item.label}
                  href={item.href}
                  onClick={() => setMobileOpen(false)}
                  className={cn(
                    "flex items-center px-4 py-3.5 text-base font-semibold rounded-lg transition-colors",
                    location === item.href ||
                      (item.href !== "/" && location.startsWith(item.href))
                      ? "text-navy bg-sand"
                      : "text-foreground hover:text-navy hover:bg-sand/50"
                  )}
                >
                  {item.label}
                </Link>
              )
            )}

            {/* Mobile bottom CTA */}
            <div className="mt-3 pt-3 border-t border-border flex flex-col gap-3 px-4">
              <a
                href="tel:+46841026111"
                className="flex items-center gap-2.5 py-3 text-foreground font-medium min-h-[44px]"
              >
                <Phone className="w-5 h-5 text-rose" />
                <span>08-410 26 111</span>
              </a>
              <Button
                onClick={() => {
                  openOffert("header-mobile");
                  setMobileOpen(false);
                }}
                className="bg-rose hover:bg-rose-dark text-white font-medium w-full py-6 text-base"
              >
                Ta kontakt
              </Button>
            </div>
          </nav>
        </div>
      </div>

      {/* Mobile overlay backdrop */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 top-[120px] bg-black/20 z-[-1]"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}
    </header>
  );
}
